import React, {useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Fade,
    Flex,
    GridItem,
    Img,
    List,
    ListItem,
    Text,
    useStyleConfig,
} from '@chakra-ui/react'
import {useIntl, FormattedMessage} from 'react-intl'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {CheckIcon} from '@salesforce/retail-react-app/app/components/icons'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import CurrentPrice from '../../../display-price/current-price'
import AddToCartButton from '../../../add-to-cart-button'
import {API_ERROR_MESSAGE} from '../../../../constants'
import {useProducts} from '@salesforce/commerce-sdk-react'
import parse from 'html-react-parser'

export const PricingTile = ({isBestSeller, timing = '', productData}) => {
    const styles = useStyleConfig('Pricing')
    const {currency: activeCurrency} = useCurrency()
    const navigate = useNavigation()
    const [addedStates, setAddedStates] = useState({})
    const [showErrorMessage, setShowErrorMessage] = useState(0)
    const errorContainerRef = useRef(null)
    const {formatMessage} = useIntl()

    var productSetProductsQty = []
    productData?.c_productSetProducts?.forEach((p) => {
        productSetProductsQty[p.productId] = p.quantity
    })
    const {data: childProductList} = useProducts(
        {
            parameters: {
                ids: Object.keys(productSetProductsQty).join(','),
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'pricing-tile-index',
            },
        },
        {
            enabled: Object.keys(productSetProductsQty)?.length > 0,
        },
    )

    const handleClick = async (itemsAdded) => {
        if (!itemsAdded) return
        setAddedStates((prev) => {
            const updatedState = {...prev}
            itemsAdded?.forEach((product) => {
                updatedState[product?.id] = true
            })
            navigate('/cart')
            return updatedState
        })
    }

    if (!productData) return null

    const images = productData?.imageGroups?.find((group) => group.viewType == 'large')?.images
    const image = images && images?.length > 0 ? images[0] : {}

    const getPrice = (customProperty) => {
        try {
            if (!customProperty) throw 'undefined'
            let productPriceList = JSON.parse(customProperty)
            if (typeof productPriceList !== 'object') throw 'no object'
            var price = 0
            Object.keys(productPriceList).forEach((productId) => {
                let unitPrice = productPriceList[productId]?.price || '0'
                let quantity = productPriceList[productId]?.quantity || '0'

                price += parseFloat(unitPrice) * parseInt(quantity)
            })
            return price
        } catch (e) {
            return 0
        }
    }

    const hasParentDiscount = productData?.c_pricesFirst && productData?.c_pricesRecurring

    // search at least one product child out of stock
    const isOutOfStock =
        childProductList?.data?.find(
            (p) => (p.inventory?.stockLevel || 0) < productSetProductsQty[p.id],
        ) !== undefined

    // set a property for NotifyMeButton
    if (isOutOfStock) {
        productData['representedProduct'] = {id: productData.id}
    }

    return (
        <GridItem
            data-testid="pricing-tile"
            className="pricing-tile"
            {...styles.tile}
            {...(isBestSeller && styles.tile?.active)}
        >
            {isBestSeller && (
                <Flex {...styles.tile?.label}>
                    <Text as="h2">
                        {formatMessage({
                            id: 'subscription.best-seller',
                            defaultMessage: 'Best Seller',
                        })}
                    </Text>
                </Flex>
            )}
            <Text as="h3" className="pricing-resizable" {...styles.tile?.title}>
                {productData.name || <>&nbsp;</>}
            </Text>
            <Flex className="pricing-resizable" {...styles.tile?.pricecontainer}>
                {productData?.c_pricesFirst && (
                    <>
                        <CurrentPrice
                            {...styles.tile?.pricesfirst}
                            price={getPrice(productData.c_pricesFirst)}
                            currency={productData.currency || activeCurrency}
                        />
                        <Text {...styles.tile?.timing}>
                            / {productData['c_subFrequency']}{' '}
                            {timing.concat(hasParentDiscount ? '*' : '')}
                        </Text>
                    </>
                )}
            </Flex>
            <Flex className="pricing-resizable" {...styles.tile?.monthlydiscount}>
                {hasParentDiscount && (
                    <>
                        <Flex direction={'row'}>
                            <CurrentPrice
                                {...styles.tile?.pricesrecurring}
                                price={getPrice(productData.c_pricesRecurring)}
                                currency={productData.currency || activeCurrency}
                            />
                            <Text {...styles.tile?.pricesrecurring}>
                                / {productData['c_subFrequency']} {timing}
                            </Text>
                        </Flex>
                        <Text {...styles.tile?.addendum}>
                            {formatMessage({
                                id: 'subscription.addendum',
                                defaultMessage: '* on the first order',
                            })}
                        </Text>
                    </>
                )}
            </Flex>
            <Flex className="pricing-resizable" justifyContent="center">
                <Img {...styles.tile?.image} src={image?.link || ''} alt="" />
            </Flex>
            <Box {...styles.tile?.cta}>
                {addedStates[productData.id] ? (
                    <Button variant={'inverted-pressed'} w={'full'} cursor={'default'}>
                        <>
                            <CheckIcon {...styles.tile?.checkiconbox} />
                            <FormattedMessage
                                id="add_to_cart_modal.label.add-to-cart-added"
                                defaultMessage="Added"
                            />
                        </>
                    </Button>
                ) : isOutOfStock ? (
                    <Button variant={'primary'} w={'full'} isDisabled={true}>
                        <FormattedMessage
                            id="use_product.message.out_of_stock"
                            defaultMessage="Out of stock"
                        />
                    </Button>
                ) : (
                    <AddToCartButton
                        product={productData}
                        setShowErrorMessage={setShowErrorMessage}
                        errorContainerRef={errorContainerRef}
                        showUniversalFeedback={false}
                        universalFeedback={false}
                        isSubscription={productData?.c_isSubscription === 'true'}
                        callback={() => handleClick([productData])}
                    />
                )}
            </Box>
            <List {...styles.tile?.features}>
                {productData.shortDescription && (
                    <ListItem
                        className="pricing-resizable"
                        {...styles.tile?.feature}
                        borderTop="none"
                        borderColor="none"
                        _before={{...styles.tile?.featurebefore}}
                    >
                        {parse(productData.shortDescription)}
                    </ListItem>
                )}
                {productData['c_costPerCoffee'] && (
                    <ListItem
                        className="pricing-resizable"
                        {...styles.tile?.feature}
                        borderTop="none"
                        borderColor="none"
                        _before={{...styles.tile?.featurebefore}}
                    >
                        <FormattedMessage
                            id="subscription.c_costPerCoffee"
                            defaultMessage="Cost per coffee"
                        />
                        :{' '}
                        <CurrentPrice
                            {...styles.tile?.featurep}
                            price={parseFloat(productData['c_costPerCoffee'].replace(',', '.'))}
                            currency={productData.currency || activeCurrency}
                        />
                    </ListItem>
                )}
                {productData['c_coffeePerDay'] && (
                    <ListItem
                        className="pricing-resizable"
                        {...styles.tile?.feature}
                        borderTop="none"
                        borderColor="none"
                        _before={{...styles.tile?.featurebefore}}
                    >
                        <FormattedMessage
                            id="subscription.c_coffeePerDay"
                            defaultMessage="Coffee per day"
                        />
                        : {productData['c_coffeePerDay']}
                    </ListItem>
                )}
                <ListItem
                    className="pricing-resizable"
                    {...styles.tile?.feature}
                    borderTop="none"
                    borderColor="none"
                    _before={{...styles.tile?.featurebefore}}
                >
                    <FormattedMessage
                        id="subscription.free-shipping"
                        defaultMessage="Free Shipping"
                    />
                </ListItem>
                {productData['c_subDuration'] && (
                    <ListItem
                        className="pricing-resizable"
                        {...styles.tile?.feature}
                        borderTop="none"
                        borderColor="none"
                        _before={{...styles.tile?.featurebefore}}
                    >
                        <FormattedMessage
                            id="subscription.c_subDuration"
                            defaultMessage="Monthlies duration"
                        />
                        : {productData['c_subDuration']}
                    </ListItem>
                )}
                {productData['c_subDiscountPercentage'] && (
                    <ListItem
                        className="pricing-resizable"
                        {...styles.tile?.feature}
                        borderTop="none"
                        borderColor="none"
                        _before={{...styles.tile?.featurebefore}}
                        _last={{borderBottomWidth: 0}}
                    >
                        <FormattedMessage
                            id="subscription.c_subDiscountPercentage"
                            defaultMessage="Discount"
                        />
                        : {productData['c_subDiscountPercentage']}
                    </ListItem>
                )}
            </List>
            <Box ref={errorContainerRef}>
                {showErrorMessage ? (
                    <Fade in={true}>
                        <Text color="text.danger" fontWeight={'semibold'} marginBottom={8}>
                            {formatMessage(API_ERROR_MESSAGE)}
                        </Text>
                    </Fade>
                ) : (
                    <></>
                )}
            </Box>
        </GridItem>
    )
}

PricingTile.propTypes = {
    isBestSeller: PropTypes.bool,
    timing: PropTypes.string,
    productData: PropTypes.shape(),
}
